import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleApi } from "../../apis";
import { GET_BILLING_CONSUMER_BILLS } from "../../apis/configuration";
import { useSnackbar } from "../../context/SnackbarContext";

const PAGE_LIMIT = 20;

const useBillingConsumerBills = ({ customerPhone = null, consumerNumber = null } = {}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [bills, setBills] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const cancelToken = useRef();
  const nextIdRef = useRef(null);

  const fetchBills = useCallback(async ({ reset }) => {
    if (!customerPhone || !consumerNumber) return;

    if (cancelToken.current)
      cancelToken.current.cancel("New request appears, canceling previous request.");
    cancelToken.current = axios.CancelToken.source();

    setIsLoading(true);
    try {
      const params = {
        customer_phone: customerPhone,
        consumer_number: consumerNumber,
        limit: PAGE_LIMIT,
      };
      if (!reset && nextIdRef.current != null) params.next_id = nextIdRef.current;

      const data = await handleApi(GET_BILLING_CONSUMER_BILLS, params, cancelToken.current, navigate);

      if (data?.success) {
        const incoming = data.bills || data.results || [];
        setBills((prev) => (reset ? incoming : [...prev, ...incoming]));
        setHasNext(Boolean(data.has_next));
        nextIdRef.current = data.next_id != null ? data.next_id : null;
      }
    } catch (error) {
      if (error.message === "New request appears, canceling previous request.") return;
      showSnackbar({ message: error?.response?.data?.detail || error?.response?.data?.message || "Error fetching bills", severity: "error" });
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [customerPhone, consumerNumber, navigate, showSnackbar]);

  useEffect(() => {
    if (customerPhone && consumerNumber) {
      nextIdRef.current = null;
      setBills([]);
      setHasNext(false);
      setIsInitialLoading(true);
      fetchBills({ reset: true });
    }
  }, [customerPhone, consumerNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoading) return;
    fetchBills({ reset: false });
  }, [hasNext, isLoading, fetchBills]);

  return { bills, hasNext, isLoading, isInitialLoading, handleLoadMore };
};

export default useBillingConsumerBills;
